import React, { useState, useEffect } from 'react';
import { BarChart3, FileText, Download, Calendar, TrendingUp, Truck, Users, DollarSign, Wrench } from 'lucide-react';
import saasApiService from '../../../services/saasApi';
import ExpenseForm from '../Finance/ExpenseForm';
import RevenueForm from '../Finance/RevenueForm';

interface ReportData {
  vehicleUtilization: {
    vehicle_id: number;
    vehicle_info: string;
    total_routes: number;
    total_distance: number;
    utilization_percentage: number;
  }[];
  driverPerformance: {
    driver_id: number;
    driver_name: string;
    total_routes: number;
    completed_routes: number;
    completion_rate: number;
  }[];
  financialSummary: {
    period: string;
    total_revenue: number;
    total_expenses: number;
    net_profit: number;
    profit_margin: number;
  };
  routeAnalysis: {
    total_routes: number;
    completed_routes: number;
    cancelled_routes: number;
    average_distance: number;
    average_duration: number;
  };
}

const ReportManager: React.FC = () => {
  const [activeReport, setActiveReport] = useState<'overview' | 'vehicles' | 'drivers' | 'financial' | 'routes' | 'maintenance'>('overview');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [financialSeries, setFinancialSeries] = useState<{ period: string; total_revenue: number; total_expenses: number; net_profit: number; }[]>([]);
  const [revenueSplit, setRevenueSplit] = useState<{ outsourcing: number; company: number; total: number }>({ outsourcing: 0, company: 0, total: 0 });
  const [currency, setCurrency] = useState<'PEN' | 'USD'>('PEN');
  const [locale, setLocale] = useState<'es-PE' | 'en-US'>('es-PE');
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; title?: string; lines?: string[] }>({ visible: false, x: 0, y: 0 });
  const [libsReady, setLibsReady] = useState<{ xlsx: boolean; jspdf: boolean }>({ xlsx: false, jspdf: false });
  const [overviewDaily, setOverviewDaily] = useState<{ period: string; total_revenue: number; revenue_count?: number; total_expenses: number; net_profit: number; }[]>([]);
  const [overviewGroupBy, setOverviewGroupBy] = useState<'day' | 'week' | 'month'>('day');
  // Vehicle detail modal state
  const [vehicleDetailOpen, setVehicleDetailOpen] = useState(false);
  const [vehicleDetailLoading, setVehicleDetailLoading] = useState(false);
  const [vehicleDetail, setVehicleDetail] = useState<any | null>(null);
  const [vehicleDetailTitle, setVehicleDetailTitle] = useState<string>('');
  const [expenseTypeFilter, setExpenseTypeFilter] = useState<string>('all');
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseDraft, setExpenseDraft] = useState<any | null>(null);
  const [applyFilterToChart, setApplyFilterToChart] = useState<boolean>(true);
  const [showRevenueForm, setShowRevenueForm] = useState(false);
  const [revenueDraft, setRevenueDraft] = useState<any | null>(null);

  // Maintenance summary state
  const [maintVehicles, setMaintVehicles] = useState<any[]>([]);
  const [maintLoading, setMaintLoading] = useState<boolean>(false);
  const [maintSearch, setMaintSearch] = useState<string>('');
  const [maintCategory, setMaintCategory] = useState<'all' | 'engine_oil' | 'filters' | 'gearbox_oil' | 'crown_oil' | 'valve_calibration'>('all');
  const [maintStatus, setMaintStatus] = useState<'all' | 'upcoming' | 'overdue'>('all');

  // ===== Maintenance helpers (component scope) =====
  const maintDefs: { key: string; label: string; lastKey: string; intervalKey: string }[] = [
    { key: 'engine_oil', label: 'Aceite Motor', lastKey: 'maint_engine_oil_last_odo', intervalKey: 'maint_engine_oil_interval_km' },
    { key: 'filters', label: 'Filtros', lastKey: 'maint_filters_last_odo', intervalKey: 'maint_filters_interval_km' },
    { key: 'gearbox_oil', label: 'Aceite de Caja', lastKey: 'maint_gearbox_oil_last_odo', intervalKey: 'maint_gearbox_oil_interval_km' },
    { key: 'crown_oil', label: 'Aceite de Corona', lastKey: 'maint_crown_oil_last_odo', intervalKey: 'maint_crown_oil_interval_km' },
    { key: 'valve_calibration', label: 'Calibración Válvulas', lastKey: 'maint_valve_calib_last_odo', intervalKey: 'maint_valve_calib_interval_km' },
  ];

  const calcMaint = (vehicle: any, def: any) => {
    const last = parseFloat(vehicle?.[def.lastKey] || 0);
    const interval = parseInt(vehicle?.[def.intervalKey] || 0, 10);
    const curr = parseFloat(vehicle?.mileage || 0);
    const traveled = last ? Math.max(0, curr - last) : 0;
    const remaining = interval ? Math.max(0, interval - traveled) : 0;
    const ratio = interval ? remaining / interval : 1;
    let color = 'bg-green-50 border-green-200 text-green-800';
    if (ratio <= 0.25) color = 'bg-yellow-50 border-yellow-200 text-yellow-800';
    if (remaining === 0 && traveled > 0) color = 'bg-red-50 border-red-200 text-red-800';
    return { last, interval, curr, traveled, remaining, color };
  };

  const getMaintStatus = (m: { remaining: number; traveled: number; interval: number; }): 'ok' | 'upcoming' | 'overdue' => {
    if (m.remaining === 0 && m.traveled > 0) return 'overdue';
    const ratio = m.interval ? m.remaining / m.interval : 1;
    if (ratio <= 0.25) return 'upcoming';
    return 'ok';
  };

  const fetchVehiclesForMaintenance = async () => {
    try {
      setMaintLoading(true);
      const response = await saasApiService.getVehicles();
      let vehicleData: any[] = [];
      if (response?.vehicles && Array.isArray(response.vehicles)) vehicleData = response.vehicles;
      else if (response?.data?.vehicles && Array.isArray(response.data.vehicles)) vehicleData = response.data.vehicles;
      else if (Array.isArray(response)) vehicleData = response;
      else if (response?.items && Array.isArray(response.items)) vehicleData = response.items;
      else if (response?.data && Array.isArray(response.data)) vehicleData = response.data;
      setMaintVehicles(vehicleData);
    } catch (e) {
      console.error('Error fetching vehicles for maintenance', e);
      setMaintVehicles([]);
    } finally {
      setMaintLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  useEffect(() => {
    if (activeReport === 'financial') {
      fetchFinancialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport, dateRange, groupBy]);

  // Fetch raw revenues to split outsourcing vs empresa for selected date range
  useEffect(() => {
    (async () => {
      try {
        const revs = await saasApiService.getRevenues({ start_date: dateRange.start, end_date: dateRange.end });
        const list = Array.isArray(revs) ? revs : [];
        let outsourcing = 0, company = 0;
        list.forEach((r: any) => {
          const amt = parseFloat(r?.amount || 0);
          if (r?.vehicle_id) company += amt; else company += 0;
          if (!r?.vehicle_id) outsourcing += amt; else outsourcing += 0;
        });
        setRevenueSplit({ outsourcing, company, total: outsourcing + company });
      } catch (e) {
        console.error('Error splitting revenues', e);
        setRevenueSplit({ outsourcing: 0, company: 0, total: 0 });
      }
    })();
  }, [dateRange, activeReport]);

  // Load vehicles when maintenance tab is active
  useEffect(() => {
    if (activeReport === 'maintenance') {
      fetchVehiclesForMaintenance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport]);

  // Always keep Overview series up-to-date based on its own grouping
  useEffect(() => {
    (async () => {
      try {
        const res = await saasApiService.getFinancialReport(dateRange.start, dateRange.end, overviewGroupBy);
        const rows = res?.financial_data || [];
        setOverviewDaily(rows);
      } catch (e) {
        console.error('Error fetching daily overview series', e);
        setOverviewDaily([]);
      }
    })();
  }, [dateRange, overviewGroupBy]);

  // Load export libraries on demand
  useEffect(() => {
    const ensureScript = (src: string) => new Promise<void>((resolve) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve();
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => resolve(); // fallback gracefully
      document.head.appendChild(s);
    });
    (async () => {
      await ensureScript('https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js');
      await ensureScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js');
      await ensureScript('https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.0/dist/jspdf.plugin.autotable.min.js');
      setLibsReady({ xlsx: !!(window as any).XLSX, jspdf: !!((window as any).jspdf || (window as any).jsPDF) });
    })();
  }, []);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await saasApiService.getReports(dateRange.start, dateRange.end);
      setReportData(response);
    } catch (error) {
      console.error('Error fetching report data:', error);
      // Mock data for demonstration
      setReportData({
        vehicleUtilization: [],
        driverPerformance: [],
        financialSummary: {
          period: `${dateRange.start} - ${dateRange.end}`,
          total_revenue: 0,
          total_expenses: 0,
          net_profit: 0,
          profit_margin: 0
        },
        routeAnalysis: {
          total_routes: 0,
          completed_routes: 0,
          cancelled_routes: 0,
          average_distance: 0,
          average_duration: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Compute KPI totals from detail with robust fallbacks
  const getVehicleKpis = (detail: any) => {
    if (!detail) return { revenue: 0, expenses: 0, profit: 0, kmSinceLastTire: 0 };
    const toNum = (v: any): number => {
      if (typeof v === 'number') return v;
      if (!v) return 0;
      const s = String(v).replace(/[^0-9.-]/g, '');
      const n = parseFloat(s);
      return isNaN(n) ? 0 : n;
    };
    const revSum = (detail.revenues || []).reduce((s: number, r: any) => s + toNum(r.amount ?? r.total_amount ?? r.total ?? 0), 0);
    const expSum = (detail.expenses || []).reduce((s: number, e: any) => s + toNum(e.amount ?? e.total_amount ?? e.total ?? 0), 0);
    const summaryRev = toNum(detail.summary?.total_revenue);
    const summaryExp = toNum(detail.summary?.total_expenses);
    const summaryProfit = toNum(detail.summary?.net_profit);
    const revenue = revSum || summaryRev;
    const expenses = expSum || summaryExp;
    const profitCalc = revSum - expSum;
    const profit = profitCalc || summaryProfit;
    const km = toNum(detail.kmSinceLastTire);
    return { revenue, expenses, profit, kmSinceLastTire: km };
  };

  const fetchFinancialData = async () => {
    try {
      const response = await saasApiService.getFinancialReport(dateRange.start, dateRange.end, groupBy);
      const rows = response?.financial_data || [];
      setFinancialSeries(rows);
    } catch (e) {
      console.error('Error fetching financial report', e);
      setFinancialSeries([]);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(amount);
  };

  const periodLabel = (period?: string, gb: 'day' | 'week' | 'month' | 'year' = 'day') => {
    if (!period) return '';
    try {
      if (gb === 'day') {
        // Expect YYYY-MM-DD -> show MM-DD
        return period.length >= 10 ? period.slice(5) : period;
      }
      if (gb === 'week') {
        // Expect YYYY-ww -> show Www
        const parts = period.split('-');
        return parts.length >= 2 ? `W${parts[1]}` : period;
      }
      if (gb === 'month') {
        // Expect YYYY-MM -> show YYYY-MM
        return period.length >= 7 ? period.slice(0, 7) : period;
      }
      // year
      return period;
    } catch {
      return String(period);
    }
  };

  // Build dataset for vehicle detail export (grouped by day)
  const buildVehicleDetailDataset = () => {
    if (!vehicleDetail) return { title: 'Detalle de Vehículo', headers: [] as string[], rows: [] as any[][] };
    const title = `Detalle de ${vehicleDetailTitle} (${dateRange.start} a ${dateRange.end})${applyFilterToChart && expenseTypeFilter !== 'all' ? ` · Filtro: ${expenseTypeFilter}` : ''}`;
    const map = new Map<string, { rev: number; exp: number }>();
    (vehicleDetail.revenues || []).forEach((r: any) => {
      const d = (r.service_date || '').slice(0, 10);
      if (!d) return;
      const entry = map.get(d) || { rev: 0, exp: 0 };
      entry.rev += parseFloat(r.amount || 0);
      map.set(d, entry);
    });
    (vehicleDetail.expenses || [])
      .filter((e: any) => !applyFilterToChart || expenseTypeFilter === 'all' || (e.expense_type || 'other') === expenseTypeFilter)
      .forEach((e: any) => {
      const d = (e.expense_date || '').slice(0, 10);
      if (!d) return;
      const entry = map.get(d) || { rev: 0, exp: 0 };
      entry.exp += parseFloat(e.amount || 0);
      map.set(d, entry);
    });
    const days = Array.from(map.keys()).sort();
    const rows = days.map(d => {
      const v = map.get(d)!;
      return [d, v.rev, v.exp, v.rev - v.exp];
    });
    return { title, headers: ['Fecha', 'Ingresos', 'Gastos', 'Utilidad'], rows };
  };

  const exportVehicleDetail = (format: 'pdf' | 'excel') => {
    const dataset = buildVehicleDetailDataset();
    if (dataset.headers.length === 0) return;
    const fileBase = dataset.title.replace(/\s+/g, '_').toLowerCase();
    if (format === 'excel') {
      if (libsReady.xlsx && (window as any).XLSX) {
        const XLSX = (window as any).XLSX;
        const data = [dataset.headers, ...dataset.rows];
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'DetalleVehiculo');
        XLSX.writeFile(wb, `${fileBase}.xlsx`);
      } else {
        // CSV fallback
        const lines = dataset.rows.map(r => r.join(','));
        const csv = [dataset.headers.join(','), ...lines].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileBase}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
      return;
    }
    // PDF
    if (libsReady.jspdf) {
      const jspdfNS = (window as any).jspdf || (window as any);
      const jsPDF = jspdfNS.jsPDF || jspdfNS.JSPDF || jspdfNS.jsPDF;
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      doc.setFontSize(14);
      doc.text(dataset.title, 40, 40);
      const rows = dataset.rows.map(r => r.map(c => String(c)));
      const autoTable = (doc as any).autoTable;
      if (autoTable) {
        (doc as any).autoTable({ head: [dataset.headers], body: rows, startY: 60, styles: { fontSize: 10 } });
      } else {
        let y = 60;
        doc.setFontSize(10);
        let x = 40;
        dataset.headers.forEach((h: string) => { doc.text(h, x, y); x += 120; });
        y += 16;
        rows.forEach(r => { let xx = 40; r.forEach(c => { doc.text(c, xx, y); xx += 120; }); y += 14; });
      }
      doc.save(`${fileBase}.pdf`);
      return;
    }
    window.print();
  };

  // Small inline sparkline for vehicle net profit trend
  const VehicleNetProfitSparkline: React.FC<{ rows: any[] }> = ({ rows }) => {
    if (!rows || rows.length === 0) return <div className="text-xs text-gray-500">Sin datos para graficar</div>;
    const padding = { top: 10, left: 30, right: 10, bottom: 24 };
    const W = Math.max(380, rows.length * 28);
    const H = 140;
    const chartW = W - padding.left - padding.right;
    const chartH = H - padding.top - padding.bottom;
    const profits = rows.map(r => Number(r[3]) || 0);
    const minVal = Math.min(...profits, 0);
    const maxVal = Math.max(...profits, 1);
    const xBand = chartW / Math.max(rows.length, 1);
    const toY = (v: number) => padding.top + chartH - ((v - minVal) / (maxVal - minVal || 1)) * chartH;
    const axisColor = '#e5e7eb';
    const pts = rows.map((r, i) => ({ x: padding.left + i * xBand + xBand / 2, y: toY(Number(r[3]) || 0) }));
    const pathD = pts.length ? `M ${pts[0].x} ${pts[0].y} ` + pts.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ') : '';
    return (
      <div className="overflow-x-auto">
        <svg width={W} height={H} role="img" aria-label="Mini tendencia de utilidad">
          {/* axes */}
          <line x1={padding.left} y1={padding.top + chartH} x2={padding.left + chartW} y2={padding.top + chartH} stroke={axisColor} />
          <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartH} stroke={axisColor} />
          {/* grid */}
          {[1,2,3,4].map(i => {
            const gy = padding.top + (chartH / 5) * i;
            return <line key={`g-${i}`} x1={padding.left} y1={gy} x2={padding.left + chartW} y2={gy} stroke={axisColor} strokeDasharray="4 4" />
          })}
          {pathD && (<path d={pathD} fill="none" stroke="#0ea5e9" strokeWidth={2} />)}
          {pts.map((p, i) => (
            <circle key={`pt-${i}`} cx={p.x} cy={p.y} r={3} fill="#2563eb" />
          ))}
          {rows.map((r, i) => (
            <text key={`lbl-${i}`} x={padding.left + i * xBand + xBand / 2} y={padding.top + chartH + 14} textAnchor="middle" fontSize="10" fill="#374151">
              {String(r[0]).slice(5)}
            </text>
          ))}
        </svg>
      </div>
    );
  };

  const buildExportDataset = () => {
    if (!reportData) return { title: 'Reporte', headers: [] as string[], rows: [] as any[][] };
    if (activeReport === 'financial') {
      return {
        title: `Reporte Financiero (${dateRange.start} a ${dateRange.end})`,
        headers: ['Periodo', 'Ingresos', 'Gastos', 'Utilidad'],
        rows: financialSeries.map(r => [r.period, r.total_revenue, r.total_expenses, r.net_profit])
      };
    }
    if (activeReport === 'vehicles') {
      return {
        title: `Utilización de Vehículos (${dateRange.start} a ${dateRange.end})`,
        headers: ['Vehículo', 'Rutas totales', 'Distancia total (km)', '% Utilización'],
        rows: (reportData.vehicleUtilization || []).map(v => [v.vehicle_info, v.total_routes, v.total_distance, v.utilization_percentage])
      };
    }
    if (activeReport === 'drivers') {
      return {
        title: `Rendimiento de Conductores (${dateRange.start} a ${dateRange.end})`,
        headers: ['Conductor', 'Rutas totales', 'Rutas completadas', '% Completadas'],
        rows: (reportData.driverPerformance || []).map(d => [d.driver_name, d.total_routes, d.completed_routes, d.completion_rate])
      };
    }
    if (activeReport === 'maintenance') {
      const rows: any[][] = [];
      maintVehicles.forEach((v: any) => {
        maintDefs.forEach((def) => {
          const m = calcMaint(v, def);
          rows.push([
            `${v.brand} ${v.model} (${v.license_plate})`,
            def.label,
            m.last ? `${m.last}` : '—',
            m.interval || 0,
            m.traveled || 0,
            m.remaining || 0
          ]);
        });
      });
      return {
        title: `Resumen de Mantenimientos (${new Date().toISOString().slice(0,10)})`,
        headers: ['Vehículo', 'Tipo', 'Último (km)', 'Intervalo (km)', 'Desde último (km)', 'Faltan (km)'],
        rows
      };
    }
    if (activeReport === 'routes') {
      const r = reportData.routeAnalysis;
      return {
        title: `Análisis de Rutas (${dateRange.start} a ${dateRange.end})`,
        headers: ['Rutas totales', 'Completadas', 'Canceladas', 'Distancia promedio (km)', 'Duración promedio (min)'],
        rows: [[r.total_routes, r.completed_routes, r.cancelled_routes, r.average_distance, r.average_duration]]
      };
    }
    // overview
    return {
      title: `Resumen General (${dateRange.start} a ${dateRange.end})`,
      headers: ['Métrica', 'Valor'],
      rows: [
        ['Rutas Totales', reportData.routeAnalysis.total_routes],
        ['Rutas Completadas', reportData.routeAnalysis.completed_routes],
        ['Distancia Promedio (km)', reportData.routeAnalysis.average_distance],
        ['Ganancia Neta', reportData.financialSummary.net_profit]
      ]
    };
  };

  // Vehicle detail loader (used in Vehicles tab)
  const openVehicleDetail = async (vehicleId: number, vehicleInfo: string) => {
    try {
      setVehicleDetailOpen(true);
      setVehicleDetailLoading(true);
      setVehicleDetailTitle(vehicleInfo);
      // First attempt: with date filters
      let [vehicle, summary, revenues, expenses] = await Promise.all([
        saasApiService.getVehicle(vehicleId),
        saasApiService.getFinancialSummary({ start_date: dateRange.start, end_date: dateRange.end, vehicle_id: vehicleId } as any),
        saasApiService.getRevenues({ vehicle_id: vehicleId, start_date: dateRange.start, end_date: dateRange.end }),
        saasApiService.getExpenses({ vehicle_id: vehicleId, start_date: dateRange.start, end_date: dateRange.end })
      ]);
      // If no results, retry without date filters to at least show algo
      if (((revenues || []).length === 0) && ((expenses || []).length === 0)) {
        try {
          const [revAll, expAll] = await Promise.all([
            saasApiService.getRevenues({ vehicle_id: vehicleId }),
            saasApiService.getExpenses({ vehicle_id: vehicleId })
          ]);
          if ((revAll && revAll.length) || (expAll && expAll.length)) {
            revenues = revAll || revenues;
            expenses = expAll || expenses;
          }
        } catch {}
      }
      // Strict client-side filtering by selected vehicle id to avoid cross-vehicle leakage
      const vId = (vehicle && (vehicle.id as any)) || vehicleId;
      const filterByVehicle = (arr: any[]) => (arr || []).filter((x: any) => Number(x?.vehicle_id) === Number(vId));
      revenues = filterByVehicle(revenues || []);
      expenses = filterByVehicle(expenses || []);
      const expenseByType: Record<string, number> = {};
      (expenses || []).forEach((e: any) => {
        const t = e.expense_type || 'other';
        expenseByType[t] = (expenseByType[t] || 0) + parseFloat(e.amount || 0);
      });
      // kmSinceLastTire fallbacks
      let lastTireOdo = parseFloat((vehicle && (vehicle.tire_last_service_odo as any)) || 0);
      const currOdo = parseFloat((vehicle && (vehicle.mileage as any)) || 0);
      if (!(lastTireOdo > 0)) {
        // Try infer from expenses odometer_reading for tire-related maintenance
        const tireExpenses = (expenses || []).filter((e: any) => {
          const t = (e.expense_type || '').toLowerCase();
          const m = (e.maintenance_category || '').toLowerCase();
          return t.includes('llanta') || t.includes('neum') || m.includes('llanta') || m.includes('neum') || t.includes('tire');
        });
        const lastByOdo = tireExpenses.reduce((max: number, e: any) => {
          const od = parseFloat(e.odometer_reading || 0);
          return od > max ? od : max;
        }, 0);
        if (lastByOdo > 0) lastTireOdo = lastByOdo;
      }
      const kmSinceLastTire = lastTireOdo > 0 && currOdo > lastTireOdo ? (currOdo - lastTireOdo)
        : parseFloat((vehicle && (vehicle.tire_km_accumulated as any)) || 0) || 0;
      setVehicleDetail({ vehicle, summary, revenues, expenses, expenseByType, kmSinceLastTire });
    } catch (e) {
      console.error('Error loading vehicle detail', e);
      setVehicleDetail(null);
    } finally {
      setVehicleDetailLoading(false);
    }
  };

  const exportReport = (format: 'pdf' | 'excel') => {
    const dataset = buildExportDataset();
    const fileBase = dataset.title.replace(/\s+/g, '_').toLowerCase();
    if (format === 'excel') {
      if (libsReady.xlsx && (window as any).XLSX) {
        const XLSX = (window as any).XLSX;
        const data = [dataset.headers, ...dataset.rows];
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
        XLSX.writeFile(wb, `${fileBase}.xlsx`);
        return;
      } else {
        // Fallback CSV
        const lines = dataset.rows.map(r => r.join(','));
        const csv = [dataset.headers.join(','), ...lines].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileBase}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      }
    }
    if (format === 'pdf') {
      if (libsReady.jspdf) {
        const jspdfNS = (window as any).jspdf || (window as any);
        const jsPDF = jspdfNS.jsPDF || jspdfNS.JSPDF || jspdfNS.jsPDF;
        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        doc.setFontSize(14);
        doc.text(dataset.title, 40, 40);
        const rows = dataset.rows.map(r => r.map(c => String(c)));
        const autoTable = (doc as any).autoTable;
        if (autoTable) {
          (doc as any).autoTable({
            head: [dataset.headers],
            body: rows,
            startY: 60,
            styles: { fontSize: 10 },
            headStyles: { fillColor: [66, 133, 244] }
          });
        } else {
          // Simple text fallback
          let y = 60;
          doc.setFontSize(10);
          // headers
          let x = 40;
          dataset.headers.forEach((h: string) => { doc.text(h, x, y); x += 120; });
          y += 16;
          rows.forEach(r => {
            let xx = 40;
            r.forEach(c => { doc.text(c, xx, y); xx += 120; });
            y += 14;
          });
        }
        doc.save(`${fileBase}.pdf`);
        return;
      }
      // Fallback to print current section
      const sectionId = activeReport === 'financial' ? 'financial-report-section' : activeReport === 'vehicles' ? 'vehicles-report-section' : activeReport === 'drivers' ? 'drivers-report-section' : activeReport === 'routes' ? 'routes-report-section' : 'overview-report-section';
      const content = document.getElementById(sectionId);
      if (!content) return window.print();
      const printWin = window.open('', 'PRINT', 'height=800,width=1000');
      if (!printWin) return;
      printWin.document.write(`<!doctype html><html><head><title>${dataset.title}</title>`);
      printWin.document.write('<style>body{font-family:Arial, sans-serif;padding:24px} .title{font-size:20px;margin-bottom:12px} table{width:100%;border-collapse:collapse} th,td{border:1px solid #ddd;padding:6px;text-align:left} .muted{color:#666;font-size:12px}</style>');
      printWin.document.write('</head><body>');
      printWin.document.write(`<div class=\"title\">${dataset.title}</div>`);
      const table = [`<table><thead><tr>${dataset.headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>`,
        ...dataset.rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`),
        '</tbody></table>'
      ].join('');
      printWin.document.write(table);
      printWin.document.write(`<div class=\"muted\">Generado ${new Date().toLocaleString()}</div>`);
      printWin.document.write('</body></html>');
      printWin.document.close();
      printWin.focus();
      printWin.print();
      printWin.close();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Reportes y Análisis</h1>
        <div className="flex space-x-3">
          <button 
            onClick={() => exportReport('pdf')}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center"
          >
            <Download className="h-4 w-4 mr-2" />
            PDF
          </button>
          <button 
            onClick={() => exportReport('excel')}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
          >
            <Download className="h-4 w-4 mr-2" />
            Excel
          </button>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex items-center space-x-4">
          <Calendar className="h-5 w-5 text-gray-400" />
          <label className="text-sm font-medium text-gray-700">Período:</label>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <span className="text-gray-500">hasta</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Report Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Resumen General', icon: BarChart3 },
            { id: 'vehicles', name: 'Vehículos', icon: Truck },
            { id: 'drivers', name: 'Conductores', icon: Users },
            { id: 'financial', name: 'Financiero', icon: DollarSign },
            { id: 'routes', name: 'Rutas', icon: FileText },
            { id: 'maintenance', name: 'Mantenimientos', icon: Wrench }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveReport(tab.id as any)}
              className={`${
                activeReport === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      
      {/* Overview Tab */}
      {activeReport === 'overview' && (
        <div id="overview-report-section" className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Resumen General</h3>
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="rounded-lg border p-4 bg-green-50">
                <div className="text-xs text-gray-600">Ingresos</div>
                <div className="text-2xl font-semibold text-green-700">
                  {currency === 'PEN' ? 'S/' : '$'}{Number(reportData?.financialSummary?.total_revenue || 0).toLocaleString(locale)}
                </div>
              </div>
              <div className="rounded-lg border p-4 bg-red-50">
                <div className="text-xs text-gray-600">Gastos</div>
                <div className="text-2xl font-semibold text-red-700">
                  {currency === 'PEN' ? 'S/' : '$'}{Number(reportData?.financialSummary?.total_expenses || 0).toLocaleString(locale)}
                </div>
              </div>
              <div className="rounded-lg border p-4 bg-blue-50">
                <div className="text-xs text-gray-600">Ganancia Neta</div>
                <div className="text-2xl font-semibold text-blue-700">
                  {currency === 'PEN' ? 'S/' : '$'}{Number(reportData?.financialSummary?.net_profit || 0).toLocaleString(locale)}
                </div>
              </div>
            </div>

            {/* Daily/period table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Período</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingresos</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gastos</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ganancia</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(overviewDaily || []).length === 0 ? (
                    <tr>
                      <td className="px-3 py-3 text-sm text-gray-500" colSpan={4}>Sin datos en el periodo seleccionado.</td>
                    </tr>
                  ) : (
                    overviewDaily.map((row, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 text-sm text-gray-700">{row.period}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{currency === 'PEN' ? 'S/' : '$'}{Number(row.total_revenue || 0).toLocaleString(locale)}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{currency === 'PEN' ? 'S/' : '$'}{Number(row.total_expenses || 0).toLocaleString(locale)}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{currency === 'PEN' ? 'S/' : '$'}{Number(row.net_profit || 0).toLocaleString(locale)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="text-xs text-gray-500 mt-2">
                Agrupación: {overviewGroupBy === 'day' ? 'Diaria' : overviewGroupBy === 'week' ? 'Semanal' : 'Mensual'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Summary Tab */}
      {activeReport === 'maintenance' && (
        <div id="maintenance-report-section" className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Resumen de Mantenimientos</h3>
              <button
                className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={async () => {
                  try {
                    const res = await saasApiService.recomputeMaintenanceLastOdo();
                    await fetchVehiclesForMaintenance();
                    window.alert(`Recalculado. Actualizaciones: ${res?.updated ?? 0}`);
                  } catch (e) {
                    console.error('Error recomputing maintenance last odo', e);
                    window.alert('No se pudo recalcular.');
                  }
                }}
              >
                Recalcular últimos
              </button>
            </div>
            {/* Filters */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Buscar por vehículo o placa..."
                  value={maintSearch}
                  onChange={(e) => setMaintSearch(e.target.value)}
                  className="w-full sm:w-80 border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={maintCategory}
                  onChange={(e) => setMaintCategory(e.target.value as any)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                  title="Categoría"
                >
                  <option value="all">Todas las categorías</option>
                  {maintDefs.map(def => (
                    <option key={def.key} value={def.key as any}>{def.label}</option>
                  ))}
                </select>
                <select
                  value={maintStatus}
                  onChange={(e) => setMaintStatus(e.target.value as any)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                  title="Estado"
                >
                  <option value="all">Todos</option>
                  <option value="upcoming">Próximo</option>
                  <option value="overdue">Vencido</option>
                </select>
              </div>
            </div>

            {/* Legend */}
            {(() => {
              let upcoming = 0, overdue = 0;
              const vList = maintVehicles.filter((v:any) =>
                !maintSearch || (`${v.brand} ${v.model} ${v.license_plate}`.toLowerCase().includes(maintSearch.toLowerCase()))
              );
              vList.forEach((v:any) => {
                const defs = maintCategory === 'all' ? maintDefs : maintDefs.filter(d => d.key === maintCategory);
                defs.forEach(def => {
                  const m = calcMaint(v, def);
                  const st = getMaintStatus(m);
                  if (st === 'upcoming') upcoming += 1;
                  if (st === 'overdue') overdue += 1;
                });
              });
              return (
                <div className="mb-4 flex items-center gap-3 text-xs">
                  <span className="inline-flex items-center gap-2 px-2 py-1 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded">Próximo: {upcoming}</span>
                  <span className="inline-flex items-center gap-2 px-2 py-1 bg-red-50 border border-red-200 text-red-800 rounded">Vencido: {overdue}</span>
                </div>
              );
            })()}
            {maintLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : maintVehicles.length === 0 ? (
              <div className="text-sm text-gray-600">No hay vehículos para mostrar.</div>
            ) : (
              <div className="space-y-6">
                {maintVehicles
                  .filter((v:any) => !maintSearch || (`${v.brand} ${v.model} ${v.license_plate}`.toLowerCase().includes(maintSearch.toLowerCase())))
                  .map((v: any) => (
                  <div key={v.id} className="border border-gray-200 rounded-lg">
                    <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-800">
                        {v.brand} {v.model} · {v.license_plate} · {Number(v.mileage || 0).toLocaleString()} km
                      </div>
                    </div>
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {(maintCategory === 'all' ? maintDefs : maintDefs.filter(d => d.key === maintCategory)).map((def) => {
                        const m = calcMaint(v, def);
                        const st = getMaintStatus(m);
                        if (maintStatus !== 'all' && st !== maintStatus) return null;
                        const strongColor = st === 'overdue' ? 'ring-2 ring-red-400' : st === 'upcoming' ? 'ring-2 ring-yellow-300' : '';
                        return (
                          <div key={def.key} className={`border rounded-md p-3 text-xs ${m.color} ${strongColor}`}>
                            <div className="flex items-center justify-between">
                              <span className="font-semibold">{def.label}</span>
                              <button
                                className="ml-2 px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                                onClick={() => {
                                  setExpenseDraft({
                                    vehicle_id: v.id,
                                    expense_type: 'maintenance',
                                    maintenance_category: def.key,
                                    expense_date: new Date().toISOString().split('T')[0],
                                    odometer_reading: v.mileage
                                  });
                                  setShowExpenseForm(true);
                                }}
                              >
                                Registrar
                              </button>
                            </div>
                            <div className="mt-1">Último: {m.last ? `${m.last.toLocaleString()} km` : '—'}</div>
                            <div>Desde último: {m.traveled.toLocaleString()} km</div>
                            <div>Faltan: {m.remaining.toLocaleString()} km</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Expense Form Modal prefilled from vehicle */}
      <ExpenseForm
        isOpen={showExpenseForm}
        onClose={() => {
          setShowExpenseForm(false);
          setExpenseDraft(null);
        }}
        onSave={async () => {
          try {
            setShowExpenseForm(false);
            const vid = vehicleDetail?.vehicle?.id;
            if (vid) {
              // Refresh detail to reflect new expense
              await openVehicleDetail(vid, vehicleDetailTitle || '');
            }
          } finally {
            setExpenseDraft(null);
          }
        }}
        expense={expenseDraft}
      />

      {/* Revenue Form Modal prefilled from vehicle */}
      <RevenueForm
        isOpen={showRevenueForm}
        onClose={() => {
          setShowRevenueForm(false);
          setRevenueDraft(null);
        }}
        onSave={async () => {
          try {
            setShowRevenueForm(false);
            const vid = vehicleDetail?.vehicle?.id;
            if (vid) {
              // Refresh detail to reflect new revenue
              await openVehicleDetail(vid, vehicleDetailTitle || '');
            }
          } finally {
            setRevenueDraft(null);
          }
        }}
        revenue={revenueDraft}
      />

      {/* Vehicle Detail Modal (global) */}
      {vehicleDetailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setVehicleDetailOpen(false)} />
          <div className="relative bg-white w-full max-w-3xl mx-4 rounded-lg shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{vehicleDetailTitle || 'Detalle del vehículo'}</h3>
                <p className="text-xs text-gray-500">Período: {dateRange.start} a {dateRange.end}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={() => exportVehicleDetail('pdf')}
                  title="Exportar PDF"
                >
                  <FileText className="h-4 w-4" /> PDF
                </button>
                <button
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={() => exportVehicleDetail('excel')}
                  title="Exportar Excel"
                >
                  <Download className="h-4 w-4" /> Excel
                </button>
                <button className="px-2 py-1 text-sm text-gray-600 hover:text-gray-900" onClick={() => setVehicleDetailOpen(false)}>Cerrar</button>
              </div>
            </div>
            <div className="p-4 max-h-[80vh] overflow-auto">
              {vehicleDetailLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : vehicleDetail ? (
                <React.Fragment>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      <div className="text-xs text-green-700">Ingresos</div>
                      <div className="text-lg font-semibold text-green-800">{formatCurrency((vehicleDetail.summary?.total_revenue ?? (vehicleDetail.revenues || []).reduce((s: number, r: any) => s + parseFloat(r.amount || 0), 0)) || 0)}</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <div className="text-xs text-red-700">Gastos</div>
                      <div className="text-lg font-semibold text-red-800">{formatCurrency((vehicleDetail.summary?.total_expenses ?? (vehicleDetail.expenses || []).reduce((s: number, e: any) => s + parseFloat(e.amount || 0), 0)) || 0)}</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <div className="text-xs text-blue-700">Utilidad Neta</div>
                      <div className="text-lg font-semibold text-blue-800">{formatCurrency(((vehicleDetail.summary?.net_profit) ?? (((vehicleDetail.summary?.total_revenue ?? (vehicleDetail.revenues || []).reduce((s: number, r: any) => s + parseFloat(r.amount || 0), 0)) || 0) - ((vehicleDetail.summary?.total_expenses ?? (vehicleDetail.expenses || []).reduce((s: number, e: any) => s + parseFloat(e.amount || 0), 0)) || 0))))}</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded p-3">
                      <div className="text-xs text-purple-700">Km desde último mantenimiento de llantas</div>
                      <div className="text-lg font-semibold text-purple-800">{(vehicleDetail.kmSinceLastTire || 0).toLocaleString()} km</div>
                    </div>
                  </div>
                </div>
                {/* Ingresos: Empresa vs Tercerización */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="text-center border rounded-lg p-4 bg-white">
                    <p className="text-sm text-gray-500">Ingresos Empresa (con tracto)</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(revenueSplit.company)}</p>
                  </div>
                  <div className="text-center border rounded-lg p-4 bg-white">
                    <p className="text-sm text-gray-500">Ingresos Tercerización (sin tracto)</p>
                    <p className="text-xl font-bold text-blue-600">{formatCurrency(revenueSplit.outsourcing)}</p>
                  </div>
                  <div className="text-center border rounded-lg p-4 bg-white">
                    <p className="text-sm text-gray-500">Ingresos Totales</p>
                    <p className="text-xl font-bold text-gray-900">{formatCurrency(revenueSplit.total)}</p>
                  </div>
                </div>
                {/* Sparkline */}
                <div className="mt-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">Tendencia de utilidad</div>
                  <VehicleNetProfitSparkline rows={buildVehicleDetailDataset().rows} />
                </div>

                {/* Filtros y tablas */}
                <div className="mt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">Detalle de ingresos y gastos</div>
                    <div className="flex items-center gap-2">
                      <button
                        className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700"
                        onClick={() => {
                          const vid = vehicleDetail?.vehicle?.id || undefined;
                          setExpenseDraft({
                            vehicle_id: vid,
                            expense_type: 'maintenance',
                            expense_date: new Date().toISOString().slice(0,10)
                          });
                          setShowExpenseForm(true);
                        }}
                      >
                        Registrar gasto
                      </button>
                      <button
                        className="px-3 py-1.5 text-xs rounded-md bg-green-600 text-white hover:bg-green-700"
                        onClick={() => {
                          const vid = vehicleDetail?.vehicle?.id || undefined;
                          setRevenueDraft({
                            vehicle_id: vid,
                            service_date: new Date().toISOString().slice(0,10),
                            status: 'pending'
                          });
                          setShowRevenueForm(true);
                        }}
                      >
                        Registrar ingreso
                      </button>
                      <label className="text-xs text-gray-600">Filtrar gasto por tipo</label>
                      <select
                        value={expenseTypeFilter}
                        onChange={(e) => setExpenseTypeFilter(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs"
                      >
                        <option value="all">Todos</option>
                        {(Array.from(new Set((vehicleDetail.expenses || []).map((e: any) => (e.expense_type || 'other') as string))) as string[])
                          .sort()
                          .map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                      </select>
                      <label className="flex items-center gap-1 text-xs text-gray-700 ml-2" title="Aplicar filtro al gráfico">
                        <input type="checkbox" checked={applyFilterToChart} onChange={(e) => setApplyFilterToChart(e.target.checked)} />
                        Aplicar al gráfico
                      </label>
                    </div>
                  </div>

                  {/* Ingresos */}
                  <div className="bg-white border rounded-md overflow-hidden">
                    <div className="px-3 py-2 text-sm font-semibold text-gray-800 bg-gray-50 border-b">Ingresos</div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-gray-600">Fecha</th>
                            <th className="px-3 py-2 text-left text-gray-600">Descripción</th>
                            <th className="px-3 py-2 text-right text-gray-600">Monto</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {(vehicleDetail.revenues || []).slice(0, 20).map((r: any, idx: number) => (
                            <tr key={idx}>
                              <td className="px-3 py-2 whitespace-nowrap">{String(r.service_date || '').slice(0,10)}</td>
                              <td className="px-3 py-2">{r.description || r.service_type || '-'}</td>
                              <td className="px-3 py-2 text-right text-green-700 font-medium">{formatCurrency(Number(r.amount || 0))}</td>
                            </tr>
                          ))}
                          {((vehicleDetail.revenues || []).length === 0) && (
                            <tr><td className="px-3 py-3 text-center text-gray-500" colSpan={3}>Sin ingresos</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Gastos */}
                  <div className="bg-white border rounded-md overflow-hidden">
                    <div className="px-3 py-2 text-sm font-semibold text-gray-800 bg-gray-50 border-b">Gastos</div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-gray-600">Fecha</th>
                            <th className="px-3 py-2 text-left text-gray-600">Tipo</th>
                            <th className="px-3 py-2 text-left text-gray-600">Descripción</th>
                            <th className="px-3 py-2 text-right text-gray-600">Monto</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {(vehicleDetail.expenses || [])
                            .filter((e: any) => expenseTypeFilter === 'all' || (e.expense_type || 'other') === expenseTypeFilter)
                            .slice(0, 20)
                            .map((e: any, idx: number) => (
                              <tr key={idx}>
                                <td className="px-3 py-2 whitespace-nowrap">{String(e.expense_date || '').slice(0,10)}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{e.expense_type || 'other'}</td>
                                <td className="px-3 py-2">{e.description || e.maintenance_category || '-'}</td>
                                <td className="px-3 py-2 text-right text-red-700 font-medium">{formatCurrency(Number(e.amount || 0))}</td>
                              </tr>
                            ))}
                          {((vehicleDetail.expenses || []).filter((e: any) => expenseTypeFilter === 'all' || (e.expense_type || 'other') === expenseTypeFilter).length === 0) && (
                            <tr><td className="px-3 py-3 text-center text-gray-500" colSpan={4}>Sin gastos</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                </React.Fragment>
              ) : (
                <div className="text-center py-10 text-sm text-gray-500">No se pudo cargar el detalle.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Utilization Tab */}
      {activeReport === 'vehicles' && (
        <div id="vehicles-report-section" className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Utilización de Vehículos
            </h3>
            {reportData?.vehicleUtilization.length === 0 ? (
              <div className="text-center py-8">
                <Truck className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No hay datos de utilización disponibles</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reportData?.vehicleUtilization.map((v, idx) => (
                  <button key={idx} className="w-full text-left flex items-center justify-between border rounded-md px-4 py-3 hover:bg-gray-50"
                    onClick={() => openVehicleDetail(v.vehicle_id, v.vehicle_info)}
                    title="Ver detalle financiero del vehículo"
                  >
                    <div>
                      <div className="font-medium text-gray-900">{v.vehicle_info}</div>
                      <div className="text-sm text-gray-500">Rutas totales: {v.total_routes} &nbsp; Distancia total: {v.total_distance} km</div>
                    </div>
                    <div className="text-sm text-gray-500">{v.utilization_percentage}% utilización</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Driver Performance Tab */}
      {activeReport === 'drivers' && (
        <div id="drivers-report-section" className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Rendimiento de Conductores
            </h3>
            {reportData?.driverPerformance.length === 0 ? (
              <div className="text-center py-8">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No hay datos de rendimiento disponibles</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reportData?.driverPerformance.map((driver, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-gray-900">{driver.driver_name}</h4>
                      <span className="text-sm text-gray-500">
                        {driver.completion_rate}% completadas
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>Rutas totales: {driver.total_routes}</div>
                      <div>Rutas completadas: {driver.completed_routes}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Financial Tab */}
      {activeReport === 'financial' && (
        <div id="financial-report-section" className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6 relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Resumen Financiero</h3>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Agrupar por</label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as any)}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  <option value="day">Día</option>
                  <option value="week">Semana</option>
                  <option value="month">Mes</option>
                  <option value="year">Año</option>
                </select>
                <label className="text-sm text-gray-600 ml-3">Moneda</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as 'PEN' | 'USD')}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  <option value="PEN">PEN</option>
                  <option value="USD">USD</option>
                </select>
                <label className="text-sm text-gray-600 ml-3">Formato</label>
                <select
                  value={locale}
                  onChange={(e) => setLocale(e.target.value as 'es-PE' | 'en-US')}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  <option value="es-PE">es-PE</option>
                  <option value="en-US">en-US</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <p className="text-sm text-gray-500">Ingresos Totales</p>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(reportData?.financialSummary.total_revenue || 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Gastos Totales</p>
                <p className="text-xl font-bold text-red-600">
                  {formatCurrency(reportData?.financialSummary.total_expenses || 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Ganancia Neta</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(reportData?.financialSummary.net_profit || 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Margen de Ganancia</p>
                <p className="text-xl font-bold text-purple-600">
                  {reportData?.financialSummary.profit_margin || 0}%
                </p>
              </div>
            </div>

            {/* Simple SVG Bar Chart */}
            <div className="overflow-x-auto">
              {financialSeries.length === 0 ? (
                <div className="text-sm text-gray-500">Sin datos para el período seleccionado.</div>
              ) : (
                <svg
                  width={Math.max(700, financialSeries.length * 80)}
                  height={320}
                  role="img"
                  aria-label="Gráfico de ingresos y gastos"
                  onMouseLeave={() => setTooltip({ visible: false, x: 0, y: 0 })}
                >
                  {(() => {
                    const padding = { top: 20, left: 50, right: 20, bottom: 60 };
                    const chartW = Math.max(700, financialSeries.length * 80) - padding.left - padding.right;
                    const chartH = 300 - padding.top - padding.bottom;
                    const maxY = Math.max(...financialSeries.map(d => Math.max(d.total_revenue, d.total_expenses)), 1);
                    const xBand = chartW / financialSeries.length;
                    const barW = xBand / 3;
                    const toY = (val: number) => padding.top + chartH - (val / maxY) * chartH;
                    const axisColor = '#e5e7eb';
                    const bars: React.ReactElement[] = [];
                    const labels: React.ReactElement[] = [];
                    const points: { x: number; y: number }[] = [];
                    financialSeries.forEach((d, i) => {
                      const x = padding.left + i * xBand;
                      // revenue bar (green)
                      const ry = toY(d.total_revenue);
                      const rh = chartH - (ry - padding.top);
                      bars.push(
                        <rect
                          key={`rev-${i}`}
                          x={x}
                          y={ry}
                          width={barW}
                          height={rh}
                          fill="#16a34a"
                          onMouseEnter={(e) => setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, title: `${d.period} · Ingresos`, lines: [formatCurrency(d.total_revenue)] })}
                          onMouseMove={(e) => setTooltip((t) => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }))}
                        />
                      );
                      // expense bar (red)
                      const ex = x + barW + 6;
                      const ey = toY(d.total_expenses);
                      const eh = chartH - (ey - padding.top);
                      bars.push(
                        <rect
                          key={`exp-${i}`}
                          x={ex}
                          y={ey}
                          width={barW}
                          height={eh}
                          fill="#dc2626"
                          onMouseEnter={(e) => setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, title: `${d.period} · Gastos`, lines: [formatCurrency(d.total_expenses)] })}
                          onMouseMove={(e) => setTooltip((t) => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }))}
                        />
                      );
                      // net profit point (line will connect these)
                      const cx = x + xBand / 2;
                      const cy = toY(d.net_profit);
                      points.push({ x: cx, y: cy });
                      bars.push(
                        <circle
                          key={`pt-${i}`}
                          cx={cx}
                          cy={cy}
                          r={3}
                          fill="#2563eb"
                          onMouseEnter={(e) => setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, title: `${d.period} · Utilidad`, lines: [formatCurrency(d.net_profit)] })}
                          onMouseMove={(e) => setTooltip((t) => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }))}
                        />
                      );
                      // period label
                      labels.push(
                        <text key={`lbl-${i}`} x={x + xBand / 2} y={padding.top + chartH + 20} textAnchor="middle" fontSize="12" fill="#374151">
                          {d.period}
                        </text>
                      );
                    });
                    // build net profit path
                    const pathD = points.length
                      ? `M ${points[0].x} ${points[0].y} ` + points.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ')
                      : '';
                    // axes
                    const axis = (
                      <g key="axes">
                        <line x1={padding.left} y1={padding.top + chartH} x2={padding.left + chartW} y2={padding.top + chartH} stroke={axisColor} />
                        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartH} stroke={axisColor} />
                      </g>
                    );
                    // legend
                    const legend = (
                      <g key="legend">
                        <rect x={padding.left} y={5} width={12} height={12} fill="#16a34a" />
                        <text x={padding.left + 18} y={15} fontSize="12" fill="#374151">Ingresos</text>
                        <rect x={padding.left + 90} y={5} width={12} height={12} fill="#dc2626" />
                        <text x={padding.left + 108} y={15} fontSize="12" fill="#374151">Gastos</text>
                        <line x1={padding.left + 180} y1={11} x2={padding.left + 192} y2={11} stroke="#2563eb" strokeWidth={2} />
                        <text x={padding.left + 200} y={15} fontSize="12" fill="#374151">Utilidad</text>
                      </g>
                    );
                    return <g>{axis}{bars}{labels}{legend}{pathD && (<path d={pathD} fill="none" stroke="#2563eb" strokeWidth={2} />)}</g>;
                  })()}
                </svg>
              )}
            </div>

            {tooltip.visible && (
              <div
                style={{ position: 'fixed', left: tooltip.x, top: tooltip.y, zIndex: 50 }}
                className="pointer-events-none bg-white/95 backdrop-blur border border-gray-200 shadow-lg rounded px-3 py-2 text-xs text-gray-800"
              >
                {tooltip.title && <div className="font-semibold mb-1">{tooltip.title}</div>}
                {tooltip.lines?.map((ln, i) => (
                  <div key={i}>{ln}</div>
                ))}
              </div>
            )}

            {/* Table view */}
            {financialSeries.length > 0 && (
              <div className="mt-6 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Periodo</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingresos</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gastos</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilidad</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {financialSeries.map((r, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{r.period}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-green-700">{formatCurrency(r.total_revenue)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-red-700">{formatCurrency(r.total_expenses)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-blue-700">{formatCurrency(r.net_profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-4 py-2 text-sm font-semibold text-gray-700">Totales</td>
                      <td className="px-4 py-2 text-sm font-semibold text-green-700">
                        {formatCurrency(financialSeries.reduce((s, r) => s + r.total_revenue, 0))}
                      </td>
                      <td className="px-4 py-2 text-sm font-semibold text-red-700">
                        {formatCurrency(financialSeries.reduce((s, r) => s + r.total_expenses, 0))}
                      </td>
                      <td className="px-4 py-2 text-sm font-semibold text-blue-700">
                        {formatCurrency(financialSeries.reduce((s, r) => s + r.net_profit, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Routes Analysis Tab */}
      {activeReport === 'routes' && (
        <div id="routes-report-section" className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Análisis de Rutas
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {reportData?.routeAnalysis.total_routes || 0}
                </div>
                <div className="text-sm text-gray-500">Rutas Totales</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {reportData?.routeAnalysis.completed_routes || 0}
                </div>
                <div className="text-sm text-gray-500">Completadas</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">
                  {reportData?.routeAnalysis.cancelled_routes || 0}
                </div>
                <div className="text-sm text-gray-500">Canceladas</div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {reportData?.routeAnalysis.average_distance || 0} km
                </div>
                <div className="text-sm text-gray-500">Distancia Promedio</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {reportData?.routeAnalysis.average_duration || 0} hrs
                </div>
                <div className="text-sm text-gray-500">Duración Promedio</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Expense form modal for registering expenses from the report modal (prefilled with vehicle)
// Placed after component to keep imports at top and avoid JSX collisions
// Note: We attach it to the default export via fragment in parent render above to keep single root

export default ReportManager;
